import React from 'react';
import apiService from '../../services/apiService';
import useAuthenticatedImageLoader from '../../hooks/useAuthenticatedImageLoader';

/**
 * Sub-componente que encapsula la carga autenticada de thumbnail.
 * Exportado para reutilización en GaleriaPickerModal.
 *
 * @param {Object} props
 * @param {number} props.fotoId - ID de la foto
 * @param {string} [props.descripcion] - Descripción de la foto (alt text)
 * @returns {JSX.Element}
 */
function AuthenticatedThumb({ fotoId, descripcion }) {
  const { ref, blobUrl, loading, failed } = useAuthenticatedImageLoader(
    () => apiService.downloadThumbnail(fotoId),
    fotoId
  );
  return (
    <div ref={ref} className="galeria-thumb-wrapper">
      {loading && !blobUrl && (
        <div className="galeria-thumb-loading"><i className="fas fa-spinner fa-spin" /></div>
      )}
      {failed && (
        <div className="galeria-thumb-error"><i className="fas fa-image" /></div>
      )}
      {blobUrl && <img src={blobUrl} alt={descripcion || ''} className="galeria-thumb" />}
    </div>
  );
}

/**
 * Thumbnail pequeño para vista lista.
 *
 * @param {Object} props
 * @param {number} props.fotoId - ID de la foto
 * @param {string} [props.descripcion] - Descripción de la foto (alt text)
 * @returns {JSX.Element}
 */
function ListThumb({ fotoId, descripcion }) {
  const { ref, blobUrl, loading, failed } = useAuthenticatedImageLoader(
    () => apiService.downloadThumbnail(fotoId),
    fotoId
  );
  return (
    <div ref={ref} className="galeria-list-thumb">
      {loading && !blobUrl && <i className="fas fa-spinner fa-spin" />}
      {failed && <i className="fas fa-image" />}
      {blobUrl && <img src={blobUrl} alt={descripcion || ''} />}
    </div>
  );
}

/**
 * Ítem individual del grid con overlay hover (ver/editar/eliminar).
 *
 * @param {Object} props
 * @param {Object} props.foto - Objeto de foto
 * @param {Function} [props.onDelete] - Callback al eliminar
 * @param {Function} [props.onView] - Callback al ver en fullscreen
 * @param {Function} [props.onEdit] - Callback al editar descripción
 * @returns {JSX.Element}
 */
function ThumbnailItem({ foto, onDelete, onView, onEdit }) {
  return (
    <div className="galeria-item">
      <AuthenticatedThumb fotoId={foto.id} descripcion={foto.descripcion} />

      <div className="galeria-item-overlay">
        <div className="overlay-actions">
          <button
            className="overlay-action-btn"
            title="Ver"
            onClick={() => onView?.(foto)}
          >
            <i className="fas fa-expand" />
          </button>
          {onEdit && (
            <button
              className="overlay-action-btn"
              title="Editar descripción"
              onClick={() => onEdit(foto)}
            >
              <i className="fas fa-pen" />
            </button>
          )}
          {onDelete && (
            <button
              className="overlay-action-btn overlay-action-btn--danger"
              title="Eliminar"
              onClick={() => onDelete(foto)}
            >
              <i className="fas fa-trash" />
            </button>
          )}
        </div>
        {foto.descripcion && <span className="overlay-desc">{foto.descripcion}</span>}
      </div>

      <div className="galeria-item-meta">
        {foto.descripcion
          ? <p className="galeria-item-desc" title={foto.descripcion}>{foto.descripcion}</p>
          : <p className="galeria-item-desc text-muted galeria-desc-empty">Sin descripción</p>
        }
        {foto.created_at && (
          <span className="galeria-item-fecha">
            {new Date(foto.created_at).toLocaleDateString('es-CL')}
          </span>
        )}
      </div>
    </div>
  );
}

/**
 * Ítem individual de la vista lista.
 *
 * @param {Object} props
 * @param {Object} props.foto - Objeto de foto
 * @param {Function} [props.onDelete] - Callback al eliminar
 * @param {Function} [props.onView] - Callback al ver en fullscreen
 * @param {Function} [props.onEdit] - Callback al editar descripción
 * @returns {JSX.Element}
 */
function ListItem({ foto, onDelete, onView, onEdit }) {
  return (
    <div className="galeria-list-item">
      <ListThumb fotoId={foto.id} descripcion={foto.descripcion} />
      <div className="galeria-list-info">
        {foto.descripcion
          ? <strong>{foto.descripcion}</strong>
          : <strong className="text-muted galeria-desc-empty">Sin descripción</strong>
        }
        {foto.created_at && (
          <span>{new Date(foto.created_at).toLocaleDateString('es-CL')}</span>
        )}
      </div>
      <div className="galeria-list-actions">
        <button
          className="btn btn-table-action btn-ghost-primary"
          title="Ver"
          onClick={() => onView?.(foto)}
        >
          <i className="fas fa-expand" />
        </button>
        {onEdit && (
          <button
            className="btn btn-table-action btn-ghost-primary"
            title="Editar descripción"
            onClick={() => onEdit(foto)}
          >
            <i className="fas fa-pen" />
          </button>
        )}
        {onDelete && (
          <button
            className="btn btn-table-action btn-ghost-danger"
            title="Eliminar"
            onClick={() => onDelete(foto)}
          >
            <i className="fas fa-trash" />
          </button>
        )}
      </div>
    </div>
  );
}

/**
 * Componente de galería con soporte para vista grid y lista.
 *
 * @param {Object} props
 * @param {Array} props.fotos - Lista de fotos
 * @param {boolean} props.loading - Estado de carga del listado
 * @param {'grid'|'list'} [props.viewMode='grid'] - Modo de vista
 * @param {Function} [props.onDelete] - Callback al eliminar foto
 * @param {Function} [props.onView] - Callback al ver foto en fullscreen
 * @param {Function} [props.onEdit] - Callback al editar descripción
 * @returns {JSX.Element}
 */
function GaleriaGrid({ fotos, loading, viewMode = 'grid', onDelete, onView, onEdit }) {
  if (loading) {
    return (
      <div className="galeria-empty">
        <i className="fas fa-spinner fa-spin fa-2x" />
        <p>Cargando fotos…</p>
      </div>
    );
  }

  if (!fotos || fotos.length === 0) {
    return (
      <div className="galeria-empty">
        <i className="fas fa-images fa-3x" />
        <p>No hay fotos en tu galería</p>
        <span>Sube tu primera foto usando el botón de arriba</span>
      </div>
    );
  }

  if (viewMode === 'list') {
    return (
      <div className="galeria-list">
        {fotos.map(foto => (
          <ListItem
            key={foto.id}
            foto={foto}
            onDelete={onDelete}
            onView={onView}
            onEdit={onEdit}
          />
        ))}
      </div>
    );
  }

  return (
    <div className="galeria-grid">
      {fotos.map(foto => (
        <ThumbnailItem
          key={foto.id}
          foto={foto}
          onDelete={onDelete}
          onView={onView}
          onEdit={onEdit}
        />
      ))}
    </div>
  );
}

export { AuthenticatedThumb };
export default GaleriaGrid;
